import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { z } from 'zod/v4';

export const fullScreenPanelPhaseEnum = z.enum(['inPage', 'lifting', 'open', 'closing']);

export type FullScreenPanelPhase = z.infer<typeof fullScreenPanelPhaseEnum>;

/** The length of the lift, as the panel's `duration-[320ms]` sets it. */
export const FULL_SCREEN_PANEL_DURATION_MS = 320;

export interface PanelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * What moves the panel, laid out at its place in the window, onto the content's place in the page: a translation
 * that puts its content's top-left corner on the page's, and the insets of a `clip-path` that shows as much of the
 * content as the page shows. In px.
 */
export interface PanelAtSource {
  translateX: number;
  translateY: number;
  clipTop: number;
  clipRight: number;
  clipBottom: number;
  clipLeft: number;
}

const NO_RECT: PanelRect = { height: 0, left: 0, top: 0, width: 0 };

function rectOf(element: HTMLElement | null): PanelRect {
  if (!element) {
    return NO_RECT;
  }
  const { height, left, top, width } = element.getBoundingClientRect();
  return { height, left, top, width };
}

/** Where the panel and its content are laid out in the window, whatever transform the panel has at the moment. */
function landedRectsOf(
  panel: HTMLElement | null,
  content: HTMLElement | null,
): { panel: PanelRect; content: PanelRect } {
  if (!panel || !content) {
    return { content: NO_RECT, panel: NO_RECT };
  }
  // A fixed panel is the offset parent of its content, and its own offsets are from the window.
  const panelRect = {
    height: panel.offsetHeight,
    left: panel.offsetLeft,
    top: panel.offsetTop,
    width: panel.offsetWidth,
  };
  return {
    content: {
      height: content.offsetHeight,
      left: panelRect.left + content.offsetLeft,
      top: panelRect.top + content.offsetTop,
      width: content.offsetWidth,
    },
    panel: panelRect,
  };
}

export function panelAtSource(source: PanelRect, panel: PanelRect, content: PanelRect): PanelAtSource {
  return {
    clipBottom: panel.top + panel.height - (content.top + source.height),
    clipLeft: content.left - panel.left,
    clipRight: panel.left + panel.width - (content.left + source.width),
    clipTop: content.top - panel.top,
    translateX: source.left - content.left,
    translateY: source.top - content.top,
  };
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface FullScreenPanelState {
  phase: FullScreenPanelPhase;
  /** Where the content sits in the page: the panel lifts from there and lands back there. */
  sourceRect: PanelRect;
  /** Puts the lifted panel on `sourceRect` while it is `lifting` or `closing`; `null` until it is measured. */
  atSource: PanelAtSource | null;
}

/**
 * The phases of the lift. The panel is laid out at its place in the window as soon as it lifts, and only moves and
 * clips onto its place in the page, so its content is laid out once rather than on every frame. `lifting` lays the
 * panel out there, measures it, then puts it on the page's place for one layout, so the switch to `open`
 * transitions from there to the window. `closing` transitions back to the place in the page, measured again as the
 * page may have scrolled, and lasts as long as the transition.
 */
export function useFullScreenPanel(
  open: boolean,
  placeholderRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
): FullScreenPanelState {
  const [phase, setPhase] = useState<FullScreenPanelPhase>(fullScreenPanelPhaseEnum.enum.inPage);
  const [sourceRect, setSourceRect] = useState<PanelRect>(NO_RECT);
  const [atSource, setAtSource] = useState<PanelAtSource | null>(null);

  useLayoutEffect(() => {
    const { inPage, lifting, closing } = fullScreenPanelPhaseEnum.enum;
    const measureAtSource = (source: PanelRect) => {
      const landed = landedRectsOf(panelRef.current, contentRef.current);
      return panelAtSource(source, landed.panel, landed.content);
    };

    if (open && phase === inPage) {
      setSourceRect(rectOf(placeholderRef.current));
      setAtSource(null);
      setPhase(lifting);
      return;
    }
    if (open && phase === lifting) {
      if (atSource === null) {
        setAtSource(measureAtSource(sourceRect));
        return;
      }
      // Reading the layout makes the browser apply the starting place, so the next change transitions from it.
      panelRef.current?.getBoundingClientRect();
      setPhase(fullScreenPanelPhaseEnum.enum.open);
      return;
    }
    if (open && phase === closing) {
      setPhase(fullScreenPanelPhaseEnum.enum.open);
      return;
    }
    if (!open && phase === lifting) {
      setPhase(inPage);
      return;
    }
    if (!open && phase === fullScreenPanelPhaseEnum.enum.open) {
      const source = rectOf(placeholderRef.current);
      setSourceRect(source);
      setAtSource(measureAtSource(source));
      setPhase(closing);
    }
  }, [atSource, contentRef, open, panelRef, phase, placeholderRef, sourceRect]);

  useEffect(() => {
    if (phase !== fullScreenPanelPhaseEnum.enum.closing) {
      return undefined;
    }
    const timer = window.setTimeout(
      () => setPhase(fullScreenPanelPhaseEnum.enum.inPage),
      prefersReducedMotion() ? 0 : FULL_SCREEN_PANEL_DURATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [phase]);

  return { atSource, phase, sourceRect };
}
